// ---------------------------------------------------------------------------
// Expenses (specs/SPEC-expenses.md): each user records their own work expenses with the receipt (gas station, taxi,
// hotel invoice…), groups them into a claim and submits it. Their manager (the role above them) confirms, Accounts
// approves and marks it paid, and may create a DRAFT vendor bill in Odoo. SAR only; no per-km mileage.
// ---------------------------------------------------------------------------
import { z } from 'zod';

export const EXPENSE_TYPES = [
  { value: 'fuel', label: 'Fuel' },
  { value: 'taxi', label: 'Taxi / transport' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'meals', label: 'Meals' },
  { value: 'site_materials', label: 'Site materials' },
  { value: 'tools', label: 'Tools' },
  { value: 'tender_documents', label: 'Tender documents' },
  { value: 'travel', label: 'Visa / travel' },
  { value: 'other', label: 'Other' },
] as const;
export type ExpenseType = (typeof EXPENSE_TYPES)[number]['value'];
export const expenseTypeLabel = (v: string) => EXPENSE_TYPES.find((t) => t.value === v)?.label ?? v;

export const CLAIM_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Waiting for manager' },
  { value: 'confirmed', label: 'Waiting for the CEO' },
  { value: 'ceo_approved', label: 'Waiting for Accounts' },
  { value: 'approved', label: 'Approved · to pay' },
  { value: 'paid', label: 'Paid' },
  { value: 'returned', label: 'Returned' },
] as const;
export type ClaimStatus = (typeof CLAIM_STATUSES)[number]['value'];
export const claimStatusLabel = (v: string) => CLAIM_STATUSES.find((s) => s.value === v)?.label ?? v;

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** A new or edited expense. The receipt is a data URL (PDF, JPG, PNG or Excel, up to 8 MB). */
export const expenseInputSchema = z.object({
  expenseDate: day,
  type: z.enum(EXPENSE_TYPES.map((t) => t.value) as [ExpenseType, ...ExpenseType[]]),
  description: z.string().trim().min(1).max(500),
  amount: z.number().positive().max(1e9),
  projectId: z.string().uuid().nullable().optional(),
  opportunityId: z.string().uuid().nullable().optional(),
  receipt: z.object({ dataUrl: z.string().max(12_000_000), fileName: z.string().trim().max(200) }).optional(),
}).strict();

export const claimInputSchema = z.object({
  title: z.string().trim().min(1).max(200),
  expenseIds: z.array(z.string().uuid()).min(1).max(200),
}).strict();

export const claimDecisionSchema = z.object({
  decision: z.enum(['confirm', 'ceo_approve', 'return', 'return_manager', 'hold', 'release', 'approve', 'paid']),
  comment: z.string().trim().max(1000).optional(),
  paidMethod: z.string().trim().max(60).optional(),
  paidRef: z.string().trim().max(100).optional(),
}).strict();

export interface ExpenseRow {
  id: string; ownerId: string; ownerName: string; claimId: string | null; claimNumber: string | null; claimStatus: ClaimStatus | null;
  expenseDate: string; type: ExpenseType; description: string; amount: number;
  projectId: string | null; projectLabel: string | null; opportunityId: string | null; opportunityLabel: string | null;
  hasReceipt: boolean; receiptName: string | null;
}

export interface ClaimRow {
  id: string; number: string; title: string; status: ClaimStatus; ownerId: string; ownerName: string;
  total: number; count: number; submittedAt: string | null; confirmedAt: string | null; approvedAt: string | null; paidAt: string | null;
  returnReason: string | null; odooBillId: number | null;
  /** On hold by the manager or Accounts (it keeps its status; released, it carries on). */
  onHold: boolean; holdReason: string | null;
}

export interface ClaimDetail extends ClaimRow {
  expenses: ExpenseRow[];
  events: { action: string; actorName: string | null; comment: string | null; at: string }[];
  paidMethod: string | null; paidRef: string | null;
  /** What this viewer may do now. */
  can: { edit: boolean; submit: boolean; confirm: boolean; ceoApprove: boolean; approve: boolean; pay: boolean; odoo: boolean; returnToManager: boolean; hold: boolean; release: boolean };
}
